import React from 'react'
import { useQueryParams, StringParam } from 'use-query-params'
import { RetailerScopeWrapper } from '@retailer-platform/dashboard/gin-and-tonic'
import { AdditionalAccessControlsProvider } from '@retailer-platform/dashboard/access-control'
import type { ScopePickerComponentProps } from '@retailer-platform/dashboard/routing'
import { usePageTitleKey } from '../../utils/domain/usePageTitleKey'
import { FeatureEntitlements } from '../../access-control/featureEntitlements'

const VEHICLES_TAB_ADDITIONAL_ACCESS_CONTROLS = [
  {
    featureEntitlementIds: [FeatureEntitlements.PartnerBulkDeliveryEnabled],
    restrictedBannersAllowed: true,
  },
]

/**
 * Custom scope picker wrapper for the Partner Pick Devices list route.
 *
 * Uses a dynamic title (Equipment Data vs. Pick Devices) depending on whether
 * the selected warehouse has the partner bulk delivery entitlement.
 *
 * When the user is on the Vehicles tab, warehouses without the partner bulk
 * delivery entitlement are greyed out in the global scope picker. This uses
 * AdditionalAccessControlsProvider which only affects the picker display —
 * it does not affect page-level access checks (DashRoute, useAccessControl)
 * since those read directly from warehouse/partner contexts, not AccessControlsContext.
 */
export const PartnerPickDevicesScopePicker: React.FC<ScopePickerComponentProps> = ({
  children,
  retailerInsufficientPermission,
}) => {
  const titleKey = usePageTitleKey()
  const [{ tab }] = useQueryParams({ tab: StringParam })

  const wrappedChildren = (
    <RetailerScopeWrapper
      titles={[titleKey]}
      retailerInsufficientPermission={retailerInsufficientPermission}
    >
      {children}
    </RetailerScopeWrapper>
  )

  if (tab !== 'vehicles') {
    return wrappedChildren
  }

  return (
    <AdditionalAccessControlsProvider
      additionalAccessControls={VEHICLES_TAB_ADDITIONAL_ACCESS_CONTROLS}
    >
      {wrappedChildren}
    </AdditionalAccessControlsProvider>
  )
}
